import * as arrow from "apache-arrow";
import { readParquet, writeParquet, Table, ParquetFile } from "parquet-wasm";
import dayjs from "./dayjs";

export type FullParsedParquetData = {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
};

/** Chunked path: no full table in memory; rowCount/getRow/columns from chunked provider. */
export type ChunkedParsedParquetData = {
  chunked: true;
  rowCount: number;
  getRow: (rowIndex: number) => Record<string, unknown>;
  columns: arrow.Field<any>[];
};

export type ParsedParquetData = FullParsedParquetData | ChunkedParsedParquetData;

export function isChunkedParsedData(
  d: ParsedParquetData
): d is ChunkedParsedParquetData {
  return "chunked" in d && d.chunked === true;
}

export function convertValueToType(value: any, type: arrow.DataType): any {
  if (value == null) return value;
  switch (type.typeId) {
    case arrow.Type.Timestamp: {
      return dayjs.utc(value).toDate();
    }
    case arrow.Type.Date:
      // Arrow Date values are days since epoch, convert to milliseconds
      try {
        const milliseconds = value * 86400000;
        const date = new Date(milliseconds);
        if (isNaN(date.getTime())) {
          console.warn("Invalid Arrow Date conversion:", value, "->", date);
          return value; // Return original value if conversion fails
        }
        return date;
      } catch (error) {
        console.warn("Error converting Arrow Date:", value, error);
        return value; // Return original value if conversion fails
      }
    case arrow.Type.Int:
    case arrow.Type.Int8:
    case arrow.Type.Int16:
    case arrow.Type.Int32:
    case arrow.Type.Int64:
    case arrow.Type.Uint8:
    case arrow.Type.Uint16:
    case arrow.Type.Uint32:
    case arrow.Type.Uint64:
    case arrow.Type.Float:
    case arrow.Type.Float16:
    case arrow.Type.Float32:
    case arrow.Type.Float64:
      return Number(value);
    case arrow.Type.Utf8:
      return value.toString();
    case arrow.Type.Bool:
      return Boolean(value);
    default:
      return value; // fallback: return as-is
  }
}

/** Fetch URL as Blob; on 403/Forbidden retry once with getRefreshUrl() if provided. */
export async function fetchParquetAsBlob(
  url: string,
  getRefreshUrl?: () => Promise<string | null>
): Promise<Blob> {
  const doFetch = async (targetUrl: string): Promise<Blob> => {
    const res = await fetch(targetUrl);
    if (!res.ok) {
      throw new Error(`Failed to download: ${res.status} ${res.statusText}`);
    }
    return res.blob();
  };
  try {
    return await doFetch(url);
  } catch (error) {
    if (getRefreshUrl && isUrlExpiredError(error)) {
      const newUrl = await getRefreshUrl();
      if (newUrl) return await doFetch(newUrl);
    }
    throw error;
  }
}

/**
 * Open a parquet file from a URL by fetching to Blob and using ParquetFile.fromFile.
 * Caller must call .free() when done. Avoids ParquetFile.fromUrl which requires S3 feature.
 */
export async function openParquetFileFromUrl(
  url: string,
  getRefreshUrl?: () => Promise<string | null>
): Promise<InstanceType<typeof ParquetFile>> {
  const blob = await fetchParquetAsBlob(url, getRefreshUrl);
  return ParquetFile.fromFile(blob);
}

/**
 * Parses parquet data from a URL or base64 buffer (full read).
 * For URLs uses fetch + ParquetFile.fromFile to avoid S3 feature requirement in fromUrl.
 * Large files should use the chunked path (URL + parquetRowCount from server) instead.
 */
export const parseParquetData = async (
  parquetData: string,
  isBase64: boolean
): Promise<ParsedParquetData> => {
  try {
    if (isBase64) {
      const binaryString = atob(parquetData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const wasmTable = readParquet(bytes);
      const arrowTable = arrow.tableFromIPC(wasmTable.intoIPCStream());
      return {
        arrowTable,
        columns: arrowTable.schema.fields,
      };
    }

    // URL path: fetch as Blob then ParquetFile.fromFile (avoids fromUrl S3 feature)
    const blob = await fetchParquetAsBlob(parquetData);
    const parquetFile = await ParquetFile.fromFile(blob);
    try {
      const wasmTable = await parquetFile.read();
      const arrowTable = arrow.tableFromIPC(wasmTable.intoIPCStream());
      return {
        arrowTable,
        columns: arrowTable.schema.fields,
      };
    } finally {
      parquetFile.free();
    }
  } catch (error) {
    console.error("Error parsing parquet data:", error);
    const message =
      error instanceof Error ? error.message : "Unknown error";
    if (message.includes("unreachable") || message.includes("RuntimeError")) {
      throw new Error(
        "Data could not be loaded. Try narrowing date range or filters."
      );
    }
    throw new Error(`Failed to parse parquet data: ${message}`);
  }
};

function isUrlExpiredError(error: unknown): boolean {
  const msg =
    error instanceof Error ? error.message : String(error);
  return (
    msg.includes("403") ||
    msg.includes("Forbidden") ||
    msg.includes("Failed to download")
  );
}

/**
 * Parse parquet from a URL with optional refresh on expiry.
 * On 403/Forbidden or download failure, calls getRefreshUrl() and retries once.
 */
export const parseParquetDataWithRefresh = async (
  url: string,
  getRefreshUrl: () => Promise<string | null>
): Promise<ParsedParquetData> => {
  try {
    return await parseParquetData(url, false);
  } catch (error) {
    if (!isUrlExpiredError(error)) throw error;
    const newUrl = await getRefreshUrl();
    if (newUrl) {
      return await parseParquetData(newUrl, false);
    }
    throw new Error(
      "Download link expired. Please reopen the view or change filters to reload."
    );
  }
};

/** Result of reading a single chunk (offset, limit) from a parquet URL. */
export type ParquetChunkResult = {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
};

/**
 * Read a chunk of rows from an already-open ParquetFile (offset, limit in rows).
 * Use with openParquetFileFromUrl so the file is fetched once and reused for all chunks.
 */
export async function readChunkFromParquetFile(
  parquetFile: InstanceType<typeof ParquetFile>,
  offset: number,
  limit: number
): Promise<ParquetChunkResult> {
  const wasmTable = await parquetFile.read({ offset, limit });
  const arrowTable = arrow.tableFromIPC(wasmTable.intoIPCStream());
  return {
    arrowTable,
    columns: arrowTable.schema.fields,
  };
}

/**
 * Read a chunk of rows from a parquet URL by offset and limit.
 * Uses fetch + ParquetFile.fromFile (avoids fromUrl S3 feature). For repeated chunks
 * the chunked provider should open once with openParquetFileFromUrl and use readChunkFromParquetFile.
 */
export const readParquetChunk = async (
  url: string,
  offset: number,
  limit: number,
  getRefreshUrl: () => Promise<string | null>
): Promise<ParquetChunkResult> => {
  const parquetFile = await openParquetFileFromUrl(url, getRefreshUrl);
  try {
    return await readChunkFromParquetFile(parquetFile, offset, limit);
  } finally {
    parquetFile.free();
  }
};

/**
 * Converts an array of objects to base64-encoded Parquet format
 * This creates a proper Parquet file with PAR1 footer signature
 *
 * @param data Array of objects where keys are column names and values are cell values
 * @returns Base64-encoded string of the actual Parquet file
 */
export const arrayToParquetBase64 = (data: Record<string, any>[]): string => {
  if (data.length === 0) return "";

  // Get column names from first object
  const columns = Object.keys(data[0]);

  // Prepare column arrays for Arrow
  const columnArrays: Record<string, any[]> = {};
  columns.forEach((col) => {
    columnArrays[col] = data.map((row) => row[col]);
  });

  // Create Arrow table
  const arrowTable = arrow.tableFromArrays(columnArrays);

  // Convert Arrow table to IPC stream
  const arrowBytes = arrow.tableToIPC(arrowTable);

  // Create WASM Table instance from IPC stream
  const wasmTable = Table.fromIPCStream(new Uint8Array(arrowBytes));

  // Use parquet-wasm writeParquet with the WASM Table
  const parquetBuffer = writeParquet(wasmTable);

  // Convert to base64
  return btoa(String.fromCharCode(...new Uint8Array(parquetBuffer)));
};
